import { useState } from "react";
import { buildMasterPool, WORD_STATE } from "../../utils/words";

export function PlannerWordReport({ data, users }) {
  const children = users.filter(u => u.role === "child");
  const [childId, setChildId] = useState(children[0]?.id);
  const child = children.find(c => c.id === childId);
  const pool = buildMasterPool(data.wordLists);
  const wordMap = Object.fromEntries(pool.map(w => [w.id, w.word]));

  const spellProg = Object.entries(data.progress || {})
    .filter(([k]) => k.startsWith(childId) && !k.includes("_meaning_"))
    .map(([k, v]) => ({ wordId: k.replace(`${childId}_`, ""), ...v }));
  const meanProg = Object.entries(data.meaningProgress || {})
    .filter(([k]) => k.startsWith(childId))
    .map(([k, v]) => ({ wordId: k.replace(`${childId}_meaning_`, ""), ...v }));

  function bucket(progList) {
    const b = { new: [], learning: [], confident: [], mastered: [] };
    const seen = new Set(progList.map(p => p.wordId));
    pool.forEach(w => { if (!seen.has(w.id)) b.new.push(w.word); });
    progList.forEach(p => {
      const word = wordMap[p.wordId];
      if (!word) return;
      if (p.mastered || p.state === WORD_STATE.MASTERED)      b.mastered.push(word);
      else if (p.state === WORD_STATE.CONFIDENT)               b.confident.push(word);
      else if (p.state === WORD_STATE.LEARNING || p.attempts > 0) b.learning.push(word);
    });
    Object.values(b).forEach(arr => arr.sort());
    return b;
  }

  const sb = bucket(spellProg);
  const mb = bucket(meanProg);

  const doingWell  = sb.confident.length + mb.confident.length;
  const mastered   = sb.mastered.length + mb.mastered.length;

  function WordChips({ words, color }) {
    if (!words.length) return <div className="text-stone-400 text-xs italic">None yet</div>;
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {words.map(w => (
          <span key={w} className={`px-2.5 py-1 rounded-full text-xs font-bold text-white ${color}`}>{w}</span>
        ))}
      </div>
    );
  }

  function BucketSection({ title, emoji, words, color, bg }) {
    const [open, setOpen] = useState(true);
    return (
      <div className={`${bg} rounded-lg p-3 mb-2`}>
        <div className="flex justify-between items-center cursor-pointer" onClick={() => setOpen(o => !o)}>
          <div className="font-semibold text-sm">{emoji} {title} <span className="font-normal text-stone-500 text-xs">({words.length})</span></div>
          <span className="text-xs text-stone-500">{open ? "▲" : "▼"}</span>
        </div>
        {open && <WordChips words={words} color={color} />}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {children.map(c => (
          <button
            key={c.id}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              childId === c.id
                ? "bg-pink-100 text-pink-700 border-2 border-pink-300"
                : "bg-stone-100 text-stone-600 border-2 border-transparent hover:bg-stone-200"
            }`}
            onClick={() => setChildId(c.id)}
          >
            {c.avatar} {c.name}
          </button>
        ))}
      </div>

      <div className="index-card rounded-xl p-5 hover-lift mb-4">
        <div className="font-handwritten text-2xl text-stone-800 mb-3">📋 {child?.avatar} {child?.name}'s Word Report</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center mb-4">
          {[
            { label: "Struggling",  val: sb.learning.length, color: "text-red-600",    emoji: "😰" },
            { label: "Learning",    val: mb.learning.length, color: "text-amber-600",  emoji: "📖" },
            { label: "Doing Well",  val: doingWell,          color: "text-green-600",  emoji: "💪" },
            { label: "Mastered",    val: mastered,           color: "text-blue-900",   emoji: "🏆" },
          ].map(({ label, val, color, emoji }) => (
            <div key={label} className="bg-white/70 rounded-lg p-3">
              <div className="text-2xl">{emoji}</div>
              <div className={`text-2xl font-bold ${color}`}>{val}</div>
              <div className="text-xs text-stone-500 font-semibold uppercase tracking-wider">{label}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-stone-500 mt-2">Spelling + Meaning combined · {pool.length} words in pool</div>
      </div>

      <div className="sticky-yellow rounded-xl p-5 shadow-sm mb-4" style={{ transform: "rotate(-1deg)" }}>
        <div className="font-handwritten text-2xl text-stone-800 mb-3">✏️ Spelling Breakdown</div>
        <BucketSection title="Mastered"  emoji="🏆" words={sb.mastered}  color="bg-blue-900" bg="bg-white/60" />
        <BucketSection title="Confident" emoji="💪" words={sb.confident} color="bg-green-600" bg="bg-green-50" />
        <BucketSection title="Learning"  emoji="📖" words={sb.learning}  color="bg-amber-600" bg="bg-amber-50" />
        <BucketSection title="New"       emoji="🆕" words={sb.new}       color="bg-blue-600" bg="bg-white/60" />
      </div>

      <div className="sticky-blue rounded-xl p-5 shadow-sm mb-4" style={{ transform: "rotate(1deg)" }}>
        <div className="font-handwritten text-2xl text-stone-800 mb-3">📖 Meaning Breakdown</div>
        <BucketSection title="Mastered"  emoji="🏆" words={mb.mastered}  color="bg-blue-900" bg="bg-white/60" />
        <BucketSection title="Confident" emoji="💪" words={mb.confident} color="bg-green-600" bg="bg-green-50" />
        <BucketSection title="Learning"  emoji="📖" words={mb.learning}  color="bg-amber-600" bg="bg-amber-50" />
        <BucketSection title="New"       emoji="🆕" words={mb.new}       color="bg-blue-600" bg="bg-white/60" />
      </div>
    </>
  );
}
